import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationLongMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { postApplyTemplateToMatterArtifact } from '@/agent-v2/artifact-templates/fetch/post-apply-template-to-matter-artifact'
import { putMatterArtifactTemplateSelection } from '@/agent-v2/artifact-templates/fetch/put-matter-artifact-template-selection'
import { deleteMatterArtifactTemplateSelection } from '@/agent-v2/artifact-templates/fetch/delete-matter-artifact-template-selection'
import { getMatterArtifactAppliedTemplateUrl } from '@/agent-v2/artifact-templates/fetch/get-matter-artifact-applied-template-url'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { waitForUserFileProcessed } from '@/artifact-templates/utils/wait-for-user-file-processed'
import { generateDefaultDocxFilename } from '@/agent-v2/artifact-templates/utils/generate-default-filename'
import { fetchSignedUrlBlob, saveBlobAsFile } from '@/agent-v2/artifact-templates/utils/signed-url-download'
import { kFlushFailedMessage } from '@/matter-artifacts/constants'
import { downloadMatterArtifactAsDocx, fetchMatterArtifactDocxBlob } from '@/matter-artifacts/fetch/download-matter-artifact-docx'
import { uploadBlobToUserFiles } from '@/content-viewer/components/common/save-to-user-files-modal/actions/save-artifact-to-user-files'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'
import { UserFilesActions } from '@/user_files/files/store/slice'
import type { SelectedTemplate } from '@/artifact-templates/components/types'
import { ApplyTemplateStatus } from '@/artifact-templates/components/types'
import { ArtifactTemplatesActions } from '@/artifact-templates/store/slice'
import {
  selectMatterArtifactTemplateSelection,
  selectAgentV2TemplateName,
  selectFreshMatterArtifactAppliedTemplate,
} from '@/agent-v2/artifact-templates/store/selectors'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { matterPaths } from '@/routing/paths'
import type { RootState } from '@/store/store'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { kNoMatterId } from '@/analytics/constants'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackArtifactDownloadFailed, trackArtifactDownloadSuccess } from '@/analytics/utils/artifact-download-analytics'
import { ArtifactTemplateSource, ArtifactExportType, ArtifactTemplateApplyErrorType } from '@/analytics/enums/artifact-template.enums'
import { isAppliedTemplateFreshForSelection } from '@/artifact-templates/utils/applied-template-freshness'
import type { AgentV2ArtifactEditorFlushResult } from '@/agent-v2/artifact-editor/components/AgentV2ArtifactEditor'

// Ad-hoc template uploads land in the same default folder as chat uploads.
const kTemplateUploadFolderName = 'Assistant Uploads'

export type UseAgentV2ArtifactExportArgs = {
  matterArtifactVersionId: string
  matterId: string | null
  title: string
  conversationId: string
  /** Whether the export surface is active. Gates side effects like the root-folder fetch. */
  enabled: boolean
  /**
   * Whether this user may mutate template state (select, upload, apply). When false the hook is
   * read-only: select/upload are inert, no export path ever triggers an apply, and the templated
   * export surface is withheld entirely, so the caller's own plain download is the only export.
   */
  mutationsEnabled: boolean
  /**
   * Flushes any pending editor save before an export runs. A 'flushed' result carries the id of a
   * version newly persisted by the flush (null when nothing newer was saved, so the render-time id
   * is safe); a 'failed' result aborts the export because unsaved edits could not be persisted.
   * Resolves null when no editor is mounted. Exports deliver the resolved id so a blur-triggered
   * autosave racing the export can never ship stale content.
   */
  prepareExport?: () => Promise<AgentV2ArtifactEditorFlushResult | null>
}

type UseAgentV2ArtifactExportReturn = {
  template: SelectedTemplate | undefined
  // Name of a template currently uploading in the background, for the spinner chip. The upload is
  // non-blocking — the user can pick a different template while it runs.
  uploadingTemplateName?: string
  // True when this version has an applied template whose rendered docx still matches the draft —
  // drives the "Template Applied" toolbar state. Flips back off once the draft is edited.
  isTemplateApplied: boolean
  isApplyingTemplate: boolean
  isExporting: boolean
  applyTemplateModalOpen: boolean
  applyStatus: ApplyTemplateStatus
  outputFileId?: string
  onCancelApply: () => void
  onCloseApplyModal: () => void
  onRetry: () => void
  onSaveWithoutTemplate?: () => Promise<void>
  onDownloadWithoutTemplate?: () => Promise<void>
  onViewFile?: () => void
  onDownloadWithTemplate?: () => void
  // Saves a copy to matter files. Present for a template mutator only; a read-only user exports
  // through the caller's plain download instead.
  onSave?: () => void
  /** Saves the draft without its template. Absent for read-only users. */
  onSavePlain?: () => void
  onTemplateUpload: (file: File) => void
  onTemplateSelect: (template?: SelectedTemplate) => void
}

type TemplateSelectionOverride = {
  matterArtifactId: string
  template?: SelectedTemplate
}

// An explicit user pick or upload of a template that must re-render on the next export even when a
// cached applied output for it exists. Tracked separately from the display override so releasing
// the override (store confirmation) never delays it, and vice versa.
type ForceApplyMarker = {
  matterArtifactId: string
  templateUserFileId: string
}

export function useAgentV2ArtifactExport({
  matterArtifactVersionId,
  matterId,
  title,
  conversationId,
  enabled,
  mutationsEnabled,
  prepareExport,
}: UseAgentV2ArtifactExportArgs): UseAgentV2ArtifactExportReturn {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  // Ensure the matter root folder is available in the store for saving
  const rootFolderId = useAppSelector((state: RootState) => (matterId ? selectRootFolderIdForMatter(state, { matterId }) : null))

  useEffect(() => {
    if (enabled && matterId) dispatch(UserFoldersActions.fetchRootFolder({ matterId }))
  }, [enabled, matterId, dispatch])

  // The selection is sticky per Matter artifact, so resolve the owning artifact from the version,
  // then derive the displayed template from the persisted selection + the name from its UserFile.
  // Selecting the id and name separately (both primitives) keeps `template` referentially stable.
  const matterArtifactId = useAppSelector((state: RootState) => selectMatterArtifactVersionById(state, matterArtifactVersionId)?.matter_artifact_id)
  const selectionId = useAppSelector((state: RootState) => selectMatterArtifactTemplateSelection(state, { matterArtifactId }))
  const selectionName = useAppSelector((state: RootState) => selectAgentV2TemplateName(state, { templateUserFileId: selectionId }))
  const persistedTemplate = useMemo<SelectedTemplate | undefined>(
    () => (selectionId ? { fileId: selectionId, name: selectionName ?? '' } : undefined),
    [selectionId, selectionName]
  )

  // The applied record of an earlier apply for this version, but only while it still matches the
  // draft — editing the draft makes it stale and this falls back to undefined, so download/save
  // re-apply against the current content. The local confirmed selection can make this stale before
  // Redux selector hydration catches up.
  const freshAppliedTemplate = useAppSelector((state: RootState) => selectFreshMatterArtifactAppliedTemplate(state, { matterArtifactVersionId }))

  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Both keyed by artifact: this hook's artifact can change while a selection is still saving or an
  // upload is still running, so each reports only its own artifact's state.
  const [templateSelectionSavingByArtifactId, setTemplateSelectionSavingByArtifactId] = useState<Partial<Record<string, boolean>>>({})
  const isTemplateSelectionSaving = (matterArtifactId ? templateSelectionSavingByArtifactId[matterArtifactId] : false) ?? false
  const [uploadingNameByArtifactId, setUploadingNameByArtifactId] = useState<Partial<Record<string, string>>>({})
  const uploadingName = (matterArtifactId ? uploadingNameByArtifactId[matterArtifactId] : undefined) ?? null
  const [applyTemplateModalOpen, setApplyTemplateModalOpen] = useState(false)
  const [applyStatus, setApplyStatus] = useState<ApplyTemplateStatus>(ApplyTemplateStatus.APPLYING)
  const [outputFileId, setOutputFileId] = useState<string | undefined>(undefined)
  const [isSaveFlow, setIsSaveFlow] = useState(false)
  const [templateSelectionOverride, setTemplateSelectionOverride] = useState<TemplateSelectionOverride | null>(null)
  const [forceApplyFor, setForceApplyFor] = useState<ForceApplyMarker | null>(null)

  const matchingTemplateSelectionOverride = templateSelectionOverride?.matterArtifactId === matterArtifactId ? templateSelectionOverride : null
  const template = matchingTemplateSelectionOverride ? matchingTemplateSelectionOverride.template : persistedTemplate
  const activeForceApply = forceApplyFor?.matterArtifactId === matterArtifactId ? forceApplyFor : null
  // The applied record's presence (and freshness) is enough — no output path is stored
  // client-side; delivery fetches a signed URL on demand. A pending forced re-apply for the
  // effective template withholds the cached output so the export re-renders it.
  const hasFreshApplied = useMemo(() => {
    if (!freshAppliedTemplate) return false
    if (activeForceApply && activeForceApply.templateUserFileId === template?.fileId) return false
    if (matchingTemplateSelectionOverride) {
      const overrideTemplate = matchingTemplateSelectionOverride.template
      if (!overrideTemplate) return false
      if (
        !isAppliedTemplateFreshForSelection({
          appliedTemplateUserFileId: freshAppliedTemplate.template_user_file_id,
          selectedTemplateUserFileId: overrideTemplate.fileId,
        })
      ) {
        return false
      }
    }
    return true
  }, [freshAppliedTemplate, activeForceApply, template, matchingTemplateSelectionOverride])

  // Release the local override once the persisted store selection has caught up with its intent
  // (same fileId, or both empty), so later external hydration changes show through again.
  useEffect(() => {
    if (!templateSelectionOverride || templateSelectionOverride.matterArtifactId !== matterArtifactId) return
    const overrideFileId = templateSelectionOverride.template?.fileId
    const selectionMatchesOverride = overrideFileId ? overrideFileId === selectionId : !selectionId
    if (selectionMatchesOverride) setTemplateSelectionOverride(null)
  }, [templateSelectionOverride, matterArtifactId, selectionId])

  // A forced re-apply is only meaningful while its template is still the effective selection —
  // an external change (or removal) supersedes the user's pick, so drop the marker with it.
  useEffect(() => {
    if (!forceApplyFor || forceApplyFor.matterArtifactId !== matterArtifactId) return
    if (template?.fileId !== forceApplyFor.templateUserFileId) setForceApplyFor(null)
  }, [forceApplyFor, matterArtifactId, template])

  // Monotonic id for export attempts (download or save). Cancel bumps it, so an in-flight attempt
  // that compares its own id against the counter after an await knows it was abandoned and stops
  // without dispatching state or delivering a file.
  const exportAttemptRef = useRef(0)
  // The attempt currently holding the export lock, or null. Download and save are mutually
  // exclusive user actions on the same modal, so one lock covers both. Acquired synchronously on
  // entry — state guards like isApplyingTemplate only take effect after the pre-export flush
  // await, so without this a second click in that window would start a parallel export. Cancel
  // releases it immediately so a new export can start while the orphaned attempt is still settling.
  const activeExportAttemptRef = useRef<number | null>(null)
  // Aborts the current attempt's in-flight signed-URL fetch when the user cancels, so delivery
  // stops immediately instead of settling in the background. The AbortError rejection lands in
  // the existing catch blocks, whose stale-attempt guard already returns silently (cancel bumps
  // the attempt counter before aborting).
  const exportAbortControllerRef = useRef<AbortController | null>(null)
  const titleRef = useRef(title)
  titleRef.current = title

  // The user's latest template choice. Every pick or upload claims the next number; a slow
  // background upload commits its result only if it's still the latest, so a newer pick is never
  // clobbered by an earlier upload finishing late.
  const latestChoiceByArtifactIdRef = useRef<Partial<Record<string, number>>>({})
  // Numbered per artifact, so a choice on one never supersedes an upload still running for another.
  const claimLatestChoice = useCallback((claimForMatterArtifactId: string) => {
    const choice = (latestChoiceByArtifactIdRef.current[claimForMatterArtifactId] ?? 0) + 1
    latestChoiceByArtifactIdRef.current[claimForMatterArtifactId] = choice
    return () => latestChoiceByArtifactIdRef.current[claimForMatterArtifactId] === choice
  }, [])

  // Selection mutations must commit server-side in the order the user made them — a slow earlier
  // PUT/DELETE must never land after (and overwrite) a newer one. Chain every selection mutation
  // through this queue so requests are issued strictly in user order. The per-artifact latest-choice
  // guards above stay in charge of which result owns the UI.
  const selectionMutationQueueRef = useRef<Promise<unknown>>(Promise.resolve())
  const enqueueSelectionMutation = useCallback(<T>(mutation: () => Promise<T>): Promise<T> => {
    const queued = selectionMutationQueueRef.current.catch(() => undefined).then(() => mutation())
    selectionMutationQueueRef.current = queued
    return queued
  }, [])

  // Template mutations (selection PUT/DELETE, apply) change conversation-detail state on the
  // server, but a stale detail response already in flight can reconcile that state away —
  // request-id fencing only orders detail requests against each other, not against these
  // mutations. Once a mutation request settles (success or failure), force a fresh detail fetch
  // so the store re-converges on the server's truth. Fire-and-forget: it never alters the
  // mutation's own result or error handling.
  const refreshConversationDetail = useCallback(() => {
    dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
  }, [dispatch, conversationId])

  // Background, non-blocking upload: show the spinner chip immediately and let the user keep going.
  const handleTemplateUpload = useCallback(
    async (file: File) => {
      // Read-only users cannot change the template selection; the upload input never renders for
      // them, so this is a defense-in-depth no-op.
      if (!mutationsEnabled || !matterArtifactId) return
      if (!matterId) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Uploading a template requires a matter.', durationMs: kToastDurationShortMs }))
        return
      }
      // `accept=".docx"` on the file input is only a picker hint — the user can still choose any file.
      // Reject non-docx here (before uploading) with a clear message; the backend stays the authority
      // for the renamed-file case. Checked before claiming a choice so it can't supersede a live upload.
      if (!file.name.toLowerCase().endsWith('.docx')) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Templates must be a Word (.docx) file.', durationMs: kToastDurationShortMs }))
        return
      }
      const isLatestChoice = claimLatestChoice(matterArtifactId)
      const isTemplateReplacement = !!template
      setUploadingNameByArtifactId((current) => ({ ...current, [matterArtifactId]: file.name }))
      setTemplateSelectionSavingByArtifactId((current) => ({ ...current, [matterArtifactId]: false }))
      try {
        const folderId = await getOrCreateUserFilesUploadFolderId({ dispatch, folderName: kTemplateUploadFolderName, getState: store.getState, matterId })
        const { fileId } = await uploadBlobToUserFiles(file, file.name, folderId, matterId, dispatch)
        // Superseded while uploading: leave the file in the drive, but don't make it the selection.
        if (!isLatestChoice()) return
        // The pipeline detects the file's type asynchronously; selecting it before then is rejected
        // ("not a DOCX") because detected_content_type isn't set. Wait for processing, bailing if a
        // newer choice supersedes this upload mid-wait.
        const processed = await waitForUserFileProcessed(fileId, isLatestChoice)
        if (processed === 'superseded' || !isLatestChoice()) return
        if (processed === 'failed') {
          dispatch(
            openGlobalToast({
              type: GlobalToastType.ERROR,
              message: "We couldn't process that template file. Please try another.",
              durationMs: kToastDurationShortMs,
            })
          )
          return
        }
        const selection = await enqueueSelectionMutation(() => putMatterArtifactTemplateSelection(matterArtifactId, fileId).finally(refreshConversationDetail))
        if (!isLatestChoice()) return
        // The confirmed response record is the source of truth for what the server persisted.
        dispatch(
          ArtifactTemplatesActions.setMatterArtifactTemplateSelection({
            matterArtifactId: selection.matter_artifact_id,
            templateUserFileId: selection.template_user_file_id,
          })
        )
        setTemplateSelectionOverride({ matterArtifactId, template: { fileId: selection.template_user_file_id, name: file.name } })
        setForceApplyFor({ matterArtifactId, templateUserFileId: selection.template_user_file_id })
        dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
        trackEvent(AnalyticsEvent.ArtifactTemplateSelected, {
          artifactVersionId: matterArtifactVersionId,
          conversationId,
          matterId: matterId ?? kNoMatterId,
          artifactTemplateSource: ArtifactTemplateSource.Upload,
          isTemplateReplacement,
        })
      } catch {
        // Don't toast for a superseded upload — a newer choice owns the UI now.
        if (isLatestChoice())
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to upload template. Please try again.', durationMs: kToastDurationShortMs }))
      } finally {
        // Only clear our own spinner — a newer choice owns the display once it supersedes us.
        if (isLatestChoice()) setUploadingNameByArtifactId((current) => ({ ...current, [matterArtifactId]: undefined }))
      }
    },
    [
      mutationsEnabled,
      matterArtifactId,
      claimLatestChoice,
      matterId,
      template,
      dispatch,
      store,
      trackEvent,
      matterArtifactVersionId,
      conversationId,
      enqueueSelectionMutation,
      refreshConversationDetail,
    ]
  )

  // Persist the selection on pick (sticky per Matter artifact). Tracks only on attach; remove emits no event.
  const handleTemplateSelect = useCallback(
    async (selectedTemplate?: SelectedTemplate) => {
      // Read-only users cannot change the template selection; the select modal never renders for
      // them, so this is a defense-in-depth no-op.
      if (!mutationsEnabled || !matterArtifactId) return
      const isLatestChoice = claimLatestChoice(matterArtifactId)
      // A manual pick supersedes this artifact's in-flight upload, and its spinner with it.
      setUploadingNameByArtifactId((current) => ({ ...current, [matterArtifactId]: undefined }))
      const isTemplateReplacement = !!template

      if (!selectedTemplate) {
        setTemplateSelectionSavingByArtifactId((current) => ({ ...current, [matterArtifactId]: true }))
        try {
          await enqueueSelectionMutation(() => deleteMatterArtifactTemplateSelection(matterArtifactId).finally(refreshConversationDetail))
          if (isLatestChoice()) {
            dispatch(ArtifactTemplatesActions.clearMatterArtifactTemplateSelection({ matterArtifactId }))
            setTemplateSelectionOverride({ matterArtifactId, template: undefined })
          }
        } catch {
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to remove template. Please try again.', durationMs: kToastDurationShortMs }))
        } finally {
          if (isLatestChoice()) setTemplateSelectionSavingByArtifactId((current) => ({ ...current, [matterArtifactId]: false }))
        }
        return
      }

      if (!matterId) return

      setTemplateSelectionSavingByArtifactId((current) => ({ ...current, [matterArtifactId]: true }))
      try {
        const selection = await enqueueSelectionMutation(() =>
          putMatterArtifactTemplateSelection(matterArtifactId, selectedTemplate.fileId).finally(refreshConversationDetail)
        )
        if (!isLatestChoice()) return
        // The confirmed response record is the source of truth for what the server persisted.
        dispatch(
          ArtifactTemplatesActions.setMatterArtifactTemplateSelection({
            matterArtifactId: selection.matter_artifact_id,
            templateUserFileId: selection.template_user_file_id,
          })
        )
        setTemplateSelectionOverride({ matterArtifactId, template: selectedTemplate })
        setForceApplyFor({ matterArtifactId, templateUserFileId: selectedTemplate.fileId })
        trackEvent(AnalyticsEvent.ArtifactTemplateSelected, {
          artifactVersionId: matterArtifactVersionId,
          conversationId,
          matterId: matterId ?? kNoMatterId,
          artifactTemplateSource: ArtifactTemplateSource.MatterFiles,
          isTemplateReplacement,
        })
      } catch {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to save template. Please try again.', durationMs: kToastDurationShortMs }))
      } finally {
        if (isLatestChoice()) setTemplateSelectionSavingByArtifactId((current) => ({ ...current, [matterArtifactId]: false }))
      }
    },
    [
      mutationsEnabled,
      matterArtifactId,
      claimLatestChoice,
      template,
      dispatch,
      trackEvent,
      matterArtifactVersionId,
      conversationId,
      matterId,
      enqueueSelectionMutation,
      refreshConversationDetail,
    ]
  )

  // Untemplated docx delivery: download the version's plain export and record the completion.
  // Shared by the explicit download-without-template action and the templated download's
  // no-template fallback. Callers own error handling.
  const deliverPlainDocxDownload = useCallback(
    async (executeVersionId: string) => {
      await downloadMatterArtifactAsDocx(executeVersionId)
      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId: executeVersionId,
        ...(matterArtifactId ? { artifactId: matterArtifactId } : {}),
        artifactExportType: ArtifactExportType.Download,
        templateApplied: false,
        conversationId,
        matterId: matterId ?? kNoMatterId,
      })
      trackArtifactDownloadSuccess(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
    },
    [trackEvent, matterArtifactId, conversationId, matterId]
  )

  const handleDownloadWithTemplate = useCallback(async () => {
    if (activeExportAttemptRef.current !== null) return
    const attempt = ++exportAttemptRef.current
    activeExportAttemptRef.current = attempt
    const abortController = new AbortController()
    exportAbortControllerRef.current = abortController
    // A stale counter after an await means cancel abandoned this attempt (or a newer one owns the
    // UI) — stop without touching modal state or delivering a file.
    const isCurrentAttempt = () => exportAttemptRef.current === attempt
    try {
      // Flush any pending editor save first — a blur-triggered autosave racing this click must not
      // let the export read the pre-save head. The flushed head (when newer) is what gets exported.
      const flushResult = (await prepareExport?.()) ?? null
      if (!isCurrentAttempt()) return
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      const executeVersionId = flushResult?.matterArtifactVersionId ?? matterArtifactVersionId

      // Already applied: serve the stored output via a fresh signed URL — no re-render. Only valid
      // while the flush produced no newer version; the cached output belongs to the render-time head.
      if (hasFreshApplied && executeVersionId === matterArtifactVersionId) {
        try {
          const { downloadUrl } = await getMatterArtifactAppliedTemplateUrl(executeVersionId)
          if (!isCurrentAttempt()) return
          const outputBlob = await fetchSignedUrlBlob(downloadUrl, abortController.signal)
          if (!isCurrentAttempt()) return
          saveBlobAsFile(outputBlob, generateDefaultDocxFilename(titleRef.current))
          // A retry can land here after apply succeeded but delivery failed — complete the open
          // modal like the apply path does (no-ops when the modal is closed).
          setApplyStatus(ApplyTemplateStatus.COMPLETE)
          setApplyTemplateModalOpen(false)
          trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
            artifactVersionId: executeVersionId,
            ...(matterArtifactId ? { artifactId: matterArtifactId } : {}),
            artifactExportType: ArtifactExportType.Download,
            templateApplied: true,
            conversationId,
            matterId: matterId ?? kNoMatterId,
          })
          trackArtifactDownloadSuccess(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
        } catch (error) {
          if (!isCurrentAttempt()) return
          const message = error instanceof Error ? error.message : 'Failed to download document. Please try again.'
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
          trackArtifactDownloadFailed(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
        }
        return
      }

      // No template to (re-)apply — the applied record's template was removed with no replacement,
      // and its cached output belongs to an older version than the flushed head. The read-only half
      // of the guard is the hook's own floor: a read-only user is never offered this handler, and
      // reaching it another way must still never start an apply. Deliver the plain docx of the
      // flushed head instead of doing nothing.
      if (!template || !mutationsEnabled) {
        try {
          await deliverPlainDocxDownload(executeVersionId)
        } catch (error) {
          if (!isCurrentAttempt()) return
          const message = error instanceof Error ? error.message : 'Failed to download document. Please try again.'
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
          trackArtifactDownloadFailed(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
        }
        return
      }

      if (isApplyingTemplate || !matterId) return

      setIsApplyingTemplate(true)
      setApplyTemplateModalOpen(true)
      setApplyStatus(ApplyTemplateStatus.APPLYING)
      setOutputFileId(undefined)
      setIsSaveFlow(false)

      const applyStartedAt = Date.now()
      let failureStage = ArtifactTemplateApplyErrorType.ApplyTemplate

      try {
        const result = await postApplyTemplateToMatterArtifact({ matterArtifactVersionId: executeVersionId, templateUserFileId: template.fileId }).finally(
          refreshConversationDetail
        )

        if (!isCurrentAttempt()) return

        // Apply persisted the record server-side; reflect it locally so a later download/save can
        // serve the output without re-applying — no full conversation refetch needed.
        dispatch(ArtifactTemplatesActions.upsertMatterArtifactAppliedTemplates([result.appliedTemplate]))
        setForceApplyFor((current) =>
          current && current.matterArtifactId === matterArtifactId && current.templateUserFileId === template.fileId ? null : current
        )

        trackEvent(AnalyticsEvent.ArtifactTemplateApplyCompleted, {
          artifactVersionId: executeVersionId,
          matterId: matterId ?? kNoMatterId,
          artifactExportType: ArtifactExportType.Download,
          templateApplyDurationMs: Date.now() - applyStartedAt,
        })

        failureStage = ArtifactTemplateApplyErrorType.DeliverOutput
        const outputBlob = await fetchSignedUrlBlob(result.downloadUrl, abortController.signal)

        if (!isCurrentAttempt()) return
        saveBlobAsFile(outputBlob, generateDefaultDocxFilename(titleRef.current))

        setIsApplyingTemplate(false)
        setApplyStatus(ApplyTemplateStatus.COMPLETE)
        setApplyTemplateModalOpen(false)

        trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId: executeVersionId,
          ...(matterArtifactId ? { artifactId: matterArtifactId } : {}),
          artifactExportType: ArtifactExportType.Download,
          templateApplied: true,
          conversationId,
          matterId: matterId ?? kNoMatterId,
        })
        trackArtifactDownloadSuccess(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
      } catch {
        // An orphaned attempt's failure is nobody's problem — the modal belongs to cancel or a
        // newer attempt now.
        if (!isCurrentAttempt()) return
        setIsApplyingTemplate(false)
        setApplyStatus(ApplyTemplateStatus.ERROR)
        // Delivery failures are not apply failures; the drop shows as ApplyCompleted without ExportCompleted
        if (failureStage !== ArtifactTemplateApplyErrorType.DeliverOutput) {
          trackEvent(AnalyticsEvent.ArtifactTemplateApplyFailed, {
            artifactVersionId: executeVersionId,
            matterId: matterId ?? kNoMatterId,
            artifactExportType: ArtifactExportType.Download,
            templateApplyDurationMs: Date.now() - applyStartedAt,
            templateApplyErrorType: failureStage,
          })
        } else {
          trackArtifactDownloadFailed(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
        }
      }
    } finally {
      // Release only when this attempt still owns the lock — cancel may have freed it already,
      // and a newer attempt may hold it now.
      if (activeExportAttemptRef.current === attempt) activeExportAttemptRef.current = null
      if (exportAbortControllerRef.current === abortController) exportAbortControllerRef.current = null
    }
  }, [
    prepareExport,
    hasFreshApplied,
    template,
    mutationsEnabled,
    isApplyingTemplate,
    matterArtifactVersionId,
    conversationId,
    matterId,
    dispatch,
    trackEvent,
    matterArtifactId,
    deliverPlainDocxDownload,
    refreshConversationDetail,
  ])

  // Upload an already-prepared docx blob into the matter's files and surface it, resolving the
  // created file id. Callers guard matterId/rootFolderId before invoking.
  const saveBlobToMatterFiles = useCallback(
    async (blob: Blob, templateApplied: boolean, exportedVersionId: string): Promise<string | null> => {
      if (!matterId || !rootFolderId) return null
      const { fileId } = await uploadBlobToUserFiles(blob, generateDefaultDocxFilename(titleRef.current), rootFolderId, matterId, dispatch)
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Saved to matter files.',
          durationMs: kToastDurationShortMs,
          withLink: { label: 'View files', href: matterPaths.userFiles.folder(matterId, rootFolderId) },
        })
      )
      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId: exportedVersionId,
        ...(matterArtifactId ? { artifactId: matterArtifactId } : {}),
        artifactExportType: ArtifactExportType.SaveToMatter,
        templateApplied,
        conversationId,
        matterId,
      })
      return fileId
    },
    [matterId, rootFolderId, dispatch, trackEvent, conversationId, matterArtifactId]
  )

  const handleSaveToMatter = useCallback(async () => {
    if (activeExportAttemptRef.current !== null || !matterId || !rootFolderId) return

    const attempt = ++exportAttemptRef.current
    activeExportAttemptRef.current = attempt
    const abortController = new AbortController()
    exportAbortControllerRef.current = abortController
    // A stale counter after an await means cancel abandoned this attempt (or a newer one owns the
    // UI) — stop without touching modal state or saving a file.
    const isCurrentAttempt = () => exportAttemptRef.current === attempt
    setIsSaving(true)

    // Function-scoped so the catch can read them
    const applyStartedAt = Date.now()
    let failureStage = ArtifactTemplateApplyErrorType.ApplyTemplate
    let isTemplatedApplyFlow = false
    let executeVersionId = matterArtifactVersionId

    try {
      // Flush any pending editor save first — a blur-triggered autosave racing this click must not
      // let the export read the pre-save head. The flushed head (when newer) is what gets exported.
      const flushResult = (await prepareExport?.()) ?? null
      if (!isCurrentAttempt()) return
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      if (flushResult?.matterArtifactVersionId) executeVersionId = flushResult.matterArtifactVersionId

      // The cached output is only valid while the flush produced no newer version; it belongs to
      // the render-time head.
      if (hasFreshApplied && executeVersionId === matterArtifactVersionId) {
        // Already applied: save the stored output directly — no re-render, no modal.
        const { downloadUrl } = await getMatterArtifactAppliedTemplateUrl(executeVersionId)
        if (!isCurrentAttempt()) return
        const blob = await fetchSignedUrlBlob(downloadUrl, abortController.signal)
        if (!isCurrentAttempt()) return
        const savedFileId = await saveBlobToMatterFiles(blob, true, executeVersionId)
        if (!isCurrentAttempt()) return
        // A retry can land here after apply succeeded but delivery failed — complete the open
        // modal like the apply path does (no-ops when the modal is closed).
        if (savedFileId) {
          setOutputFileId(savedFileId)
          setApplyStatus(ApplyTemplateStatus.COMPLETE)
        }
      } else if (template && mutationsEnabled) {
        // Same floor as the download flow: applying is a mutation, so a read-only user reaching
        // here falls through to the plain docx save below instead.
        isTemplatedApplyFlow = true
        setIsApplyingTemplate(true)
        setApplyTemplateModalOpen(true)
        setApplyStatus(ApplyTemplateStatus.APPLYING)
        setOutputFileId(undefined)
        setIsSaveFlow(true)

        const result = await postApplyTemplateToMatterArtifact({ matterArtifactVersionId: executeVersionId, templateUserFileId: template.fileId }).finally(
          refreshConversationDetail
        )

        if (!isCurrentAttempt()) return

        dispatch(ArtifactTemplatesActions.upsertMatterArtifactAppliedTemplates([result.appliedTemplate]))
        setForceApplyFor((current) =>
          current && current.matterArtifactId === matterArtifactId && current.templateUserFileId === template.fileId ? null : current
        )

        trackEvent(AnalyticsEvent.ArtifactTemplateApplyCompleted, {
          artifactVersionId: executeVersionId,
          matterId: matterId ?? kNoMatterId,
          artifactExportType: ArtifactExportType.SaveToMatter,
          templateApplyDurationMs: Date.now() - applyStartedAt,
        })

        failureStage = ArtifactTemplateApplyErrorType.DeliverOutput
        const blob = await fetchSignedUrlBlob(result.downloadUrl, abortController.signal)

        if (!isCurrentAttempt()) return

        // Accepted residual: cancel cannot stop an upload already in flight — it completes server-side.
        const { fileId } = await uploadBlobToUserFiles(blob, generateDefaultDocxFilename(titleRef.current), rootFolderId, matterId, dispatch)

        if (!isCurrentAttempt()) return

        dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
        setIsApplyingTemplate(false)
        setOutputFileId(fileId)
        setApplyStatus(ApplyTemplateStatus.COMPLETE)

        trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId: executeVersionId,
          ...(matterArtifactId ? { artifactId: matterArtifactId } : {}),
          artifactExportType: ArtifactExportType.SaveToMatter,
          templateApplied: true,
          conversationId,
          matterId: matterId ?? kNoMatterId,
        })
      } else {
        const { blob } = await fetchMatterArtifactDocxBlob(executeVersionId)
        if (!isCurrentAttempt()) return
        await saveBlobToMatterFiles(blob, false, executeVersionId)
      }
    } catch (error) {
      // An orphaned attempt's failure is nobody's problem — the modal belongs to cancel or a
      // newer attempt now.
      if (!isCurrentAttempt()) return
      if (isTemplatedApplyFlow) {
        setIsApplyingTemplate(false)
        setApplyStatus(ApplyTemplateStatus.ERROR)
        // Delivery failures are not apply failures; the drop shows as ApplyCompleted without ExportCompleted
        if (failureStage !== ArtifactTemplateApplyErrorType.DeliverOutput) {
          trackEvent(AnalyticsEvent.ArtifactTemplateApplyFailed, {
            artifactVersionId: executeVersionId,
            matterId: matterId ?? kNoMatterId,
            artifactExportType: ArtifactExportType.SaveToMatter,
            templateApplyDurationMs: Date.now() - applyStartedAt,
            templateApplyErrorType: failureStage,
          })
        }
      } else {
        const message = error instanceof Error ? error.message : 'Failed to save document. Please try again.'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
      }
    } finally {
      // Release only when this attempt still owns the lock — cancel may have freed it already
      // (and reset isSaving), and a newer attempt may hold it now.
      if (activeExportAttemptRef.current === attempt) {
        activeExportAttemptRef.current = null
        setIsSaving(false)
      }
      if (exportAbortControllerRef.current === abortController) exportAbortControllerRef.current = null
    }
  }, [
    prepareExport,
    hasFreshApplied,
    template,
    mutationsEnabled,
    matterArtifactVersionId,
    matterId,
    rootFolderId,
    dispatch,
    conversationId,
    trackEvent,
    saveBlobToMatterFiles,
    matterArtifactId,
    refreshConversationDetail,
  ])

  const handleCancelApply = () => {
    // Orphan any in-flight export (its next attempt check stops it cold) and free the lock
    // immediately so a new export can start without waiting for the abandoned one to settle.
    // The counter bump comes first so the aborted fetch's AbortError rejection is already stale
    // when it reaches the catch guards — no toast, no ERROR status.
    exportAttemptRef.current++
    activeExportAttemptRef.current = null
    exportAbortControllerRef.current?.abort()
    exportAbortControllerRef.current = null
    setIsApplyingTemplate(false)
    setApplyTemplateModalOpen(false)
    setOutputFileId(undefined)
    setIsSaving(false)
  }

  const handleCloseApplyModal = () => {
    setApplyTemplateModalOpen(false)
    setOutputFileId(undefined)
  }

  const handleRetry = useCallback(() => {
    if (isSaveFlow) {
      void handleSaveToMatter()
    } else {
      void handleDownloadWithTemplate()
    }
  }, [isSaveFlow, handleSaveToMatter, handleDownloadWithTemplate])

  const handleSaveWithoutTemplate = useCallback(async () => {
    if (activeExportAttemptRef.current !== null || !matterId || !rootFolderId) return

    const attempt = ++exportAttemptRef.current
    activeExportAttemptRef.current = attempt
    const isCurrentAttempt = () => exportAttemptRef.current === attempt
    setApplyTemplateModalOpen(false)
    setIsSaving(true)

    try {
      const flushResult = (await prepareExport?.()) ?? null
      if (!isCurrentAttempt()) return
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      const executeVersionId = flushResult?.matterArtifactVersionId ?? matterArtifactVersionId
      const { blob } = await fetchMatterArtifactDocxBlob(executeVersionId)
      if (!isCurrentAttempt()) return
      await saveBlobToMatterFiles(blob, false, executeVersionId)
    } catch (error) {
      if (!isCurrentAttempt()) return
      const message = error instanceof Error ? error.message : 'Failed to save document. Please try again.'
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
    } finally {
      if (activeExportAttemptRef.current === attempt) {
        activeExportAttemptRef.current = null
        setIsSaving(false)
      }
    }
  }, [prepareExport, matterId, rootFolderId, matterArtifactVersionId, dispatch, saveBlobToMatterFiles])

  const handleDownloadWithoutTemplate = useCallback(async () => {
    setApplyTemplateModalOpen(false)
    let executeVersionId: string | undefined
    try {
      const flushResult = (await prepareExport?.()) ?? null
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      executeVersionId = flushResult?.matterArtifactVersionId ?? matterArtifactVersionId
      await deliverPlainDocxDownload(executeVersionId)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download document. Please try again.'
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
      if (!executeVersionId) return
      trackArtifactDownloadFailed(trackEvent, { artifactVersionId: executeVersionId, artifactId: matterArtifactId })
    }
  }, [prepareExport, matterArtifactVersionId, dispatch, deliverPlainDocxDownload, trackEvent, matterArtifactId])

  const handleViewFile = useCallback(() => {
    if (matterId && rootFolderId) navigate(matterPaths.userFiles.folder(matterId, rootFolderId))
    setApplyTemplateModalOpen(false)
  }, [matterId, rootFolderId, navigate])

  return {
    template,
    uploadingTemplateName: uploadingName ?? undefined,
    onTemplateUpload: handleTemplateUpload,
    onTemplateSelect: handleTemplateSelect,
    // Only the user who controls the template exports through it: present when a template is
    // selected (apply on download) or already applied (serve the stored output), and withheld while
    // an upload is still resolving into a ready selection. A read-only user has no template control
    // and so no way to tell whether a template is on the draft; giving them a templated export would
    // hand them a document that differs from the one on screen for reasons they cannot see.
    onDownloadWithTemplate:
      mutationsEnabled && (template || hasFreshApplied) && !uploadingName && !isTemplateSelectionSaving ? handleDownloadWithTemplate : undefined,
    isTemplateApplied: hasFreshApplied,
    // Same reason: a read-only user's only export is the header's plain download.
    onSave: mutationsEnabled ? handleSaveToMatter : undefined,
    // Saves the draft without its template; used where templates are applied by Export as .docx.
    onSavePlain: mutationsEnabled ? handleSaveWithoutTemplate : undefined,
    isApplyingTemplate,
    isExporting: isApplyingTemplate || isSaving || isTemplateSelectionSaving,
    applyTemplateModalOpen,
    applyStatus,
    outputFileId,
    onCancelApply: handleCancelApply,
    onCloseApplyModal: handleCloseApplyModal,
    onRetry: handleRetry,
    onSaveWithoutTemplate: isSaveFlow ? handleSaveWithoutTemplate : undefined,
    onDownloadWithoutTemplate: !isSaveFlow ? handleDownloadWithoutTemplate : undefined,
    onViewFile: isSaveFlow ? handleViewFile : undefined,
  }
}
